import { ArrowRight, Brain, TrendingUp, Lock } from 'lucide-react';

export const SystemLearnsSection = () => {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 bg-gradient-to-b from-slate-50 dark:from-slate-900 to-white dark:to-slate-800">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
          A System That Learns From You
        </h2>
        <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
          While other AI forgets ~30% of what you teach it, Raisy remembers — versioned, cross-department, and auditable. One correction improves every bot that shares that knowledge, and the system improves every day.
        </p>
      </div>

      {/* The Learning Cycle */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
        {/* Step 1 */}
        <div className="relative" data-aos="fade-up" data-aos-delay="0">
          <div className="p-6 rounded-lg border-2 border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-900/20">
            <div className="w-10 h-10 rounded-full bg-cyan-600 text-white flex items-center justify-center font-bold mb-4">
              1
            </div>
            <h3 className="font-semibold text-slate-900 dark:text-white mb-2">You Define</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Create a workflow. Raisy asks: "What decisions need to be made?"
            </p>
          </div>
          <div className="hidden md:block absolute -right-3 top-1/2 transform -translate-y-1/2">
            <ArrowRight className="w-6 h-6 text-slate-300 dark:text-slate-600" />
          </div>
        </div>

        {/* Step 2 */}
        <div className="relative" data-aos="fade-up" data-aos-delay="100">
          <div className="p-6 rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
            <div className="w-10 h-10 rounded-full bg-slate-600 dark:bg-slate-500 text-white flex items-center justify-center font-bold mb-4">
              2
            </div>
            <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Humans Review</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Every decision starts with a human. Build a library of "good" approvals.
            </p>
          </div>
          <div className="hidden md:block absolute -right-3 top-1/2 transform -translate-y-1/2">
            <ArrowRight className="w-6 h-6 text-slate-300 dark:text-slate-600" />
          </div>
        </div>

        {/* Step 3 */}
        <div className="relative" data-aos="fade-up" data-aos-delay="200">
          <div className="p-6 rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
            <div className="w-10 h-10 rounded-full bg-slate-600 dark:bg-slate-500 text-white flex items-center justify-center font-bold mb-4">
              3
            </div>
            <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Patterns Emerge</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              After 50+ approvals, Raisy identifies what you consistently approve.
            </p>
          </div>
          <div className="hidden md:block absolute -right-3 top-1/2 transform -translate-y-1/2">
            <ArrowRight className="w-6 h-6 text-slate-300 dark:text-slate-600" />
          </div>
        </div>

        {/* Step 4 */}
        <div data-aos="fade-up" data-aos-delay="300">
          <div className="p-6 rounded-lg border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
            <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold mb-4">
              4
            </div>
            <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Autonomy Earned</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Bot reaches 90%+ confidence and is promoted to autonomous execution.
            </p>
          </div>
        </div>
      </div>

      {/* Three Core Mechanics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        {/* Confidence Score */}
        <div
          className="p-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-lg transition-shadow"
          data-aos="fade-up"
          data-aos-delay="0"
        >
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-100 to-teal-100 dark:from-cyan-900 dark:to-teal-900 flex items-center justify-center text-cyan-600 dark:text-cyan-400 mb-4">
            <TrendingUp className="w-6 h-6" />
          </div>
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Confidence Score</h3>
          <p className="text-slate-600 dark:text-slate-300 text-sm mb-4">
            Every bot has a transparent confidence metric: (approvals / total decisions) × 100.
          </p>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-600 dark:text-slate-400">Week 1</span>
              <span className="font-bold text-red-600 dark:text-red-400">0% confident</span>
            </div>
            <div className="w-full bg-red-100 dark:bg-red-900/30 rounded h-2"></div>

            <div className="flex items-center justify-between text-xs mt-3">
              <span className="text-slate-600 dark:text-slate-400">Week 3</span>
              <span className="font-bold text-yellow-600 dark:text-yellow-400">60% confident</span>
            </div>
            <div className="w-full bg-yellow-100 dark:bg-yellow-900/30 rounded h-2" style={{ width: '60%' }}></div>

            <div className="flex items-center justify-between text-xs mt-3">
              <span className="text-slate-600 dark:text-slate-400">Week 6</span>
              <span className="font-bold text-green-600 dark:text-green-400">92% confident</span>
            </div>
            <div className="w-full bg-green-100 dark:bg-green-900/30 rounded h-2" style={{ width: '92%' }}></div>
          </div>
        </div>

        {/* Feedback Loop */}
        <div
          className="p-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-lg transition-shadow"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-100 to-teal-100 dark:from-cyan-900 dark:to-teal-900 flex items-center justify-center text-cyan-600 dark:text-cyan-400 mb-4">
            <Brain className="w-6 h-6" />
          </div>
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Active Learning</h3>
          <p className="text-slate-600 dark:text-slate-300 text-sm mb-4">
            When you reject a decision, Raisy studies it. Our system generates a corrected version and learns.
          </p>
          <div className="space-y-2 text-sm">
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
              <p className="font-medium text-red-900 dark:text-red-400">❌ Reject: "Too aggressive"</p>
            </div>
            <div className="flex justify-center">
              <ArrowRight className="w-4 h-4 text-slate-400 dark:text-slate-600 rotate-90" />
            </div>
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
              <p className="font-medium text-green-900 dark:text-green-400">✓ New version generated</p>
              <p className="text-xs text-green-800 dark:text-green-400 mt-1">Next run uses your feedback</p>
            </div>
          </div>
        </div>

        {/* Audit Trail */}
        <div
          className="p-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-lg transition-shadow"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-100 to-teal-100 dark:from-cyan-900 dark:to-teal-900 flex items-center justify-center text-cyan-600 dark:text-cyan-400 mb-4">
            <Lock className="w-6 h-6" />
          </div>
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">100% Transparent</h3>
          <p className="text-slate-600 dark:text-slate-300 text-sm mb-4">
            Every decision is logged with confidence score, reasoning, and human approval.
          </p>
          <div className="space-y-2 text-xs bg-slate-50 dark:bg-slate-700 p-3 rounded border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300">
            <p><strong>Bot:</strong> Lead Qualifier</p>
            <p><strong>Decision:</strong> Qualify lead</p>
            <p><strong>Confidence:</strong> 91%</p>
            <p><strong>Human:</strong> Sarah Chen (approved)</p>
            <p><strong>Timestamp:</strong> 2026-05-02 09:34</p>
            <p><strong>Hash:</strong> 0x7f3a9c...</p>
          </div>
        </div>
      </div>

      {/* Why This Matters */}
      <div className="p-8 md:p-12 bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
        <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
          Why This Approach Wins
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-slate-700 dark:text-slate-300 mb-3">
              <strong className="text-cyan-600 dark:text-cyan-400">vs. Static Rules:</strong> Rules break when exceptions appear. Raisy learns exceptions.
            </p>
            <p className="text-slate-700 dark:text-slate-300">
              <strong className="text-cyan-600 dark:text-cyan-400">vs. Black-Box AI:</strong> You never know why decisions are made. Raisy shows you every step.
            </p>
          </div>
          <div>
            <p className="text-slate-700 dark:text-slate-300 mb-3">
              <strong className="text-cyan-600 dark:text-cyan-400">vs. Manual Everything:</strong> A manual process repeats your effort; Raisy learns from every run.
            </p>
            <p className="text-slate-700 dark:text-slate-300">
              <strong className="text-cyan-600 dark:text-cyan-400">vs. Fire-and-Forget Bots:</strong> Bots don't learn. Yours do. They improve every week.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
